import TimeMe from 'timeme.js';
import throttle from 'lodash/throttle';

import routes from 'js/core_pre_webpack/routes';
import { keptAlivePost } from 'js/utils/kept_alive_post';

export function startActivityTracking() {
  const HIT_ID = window.Aedu && window.Aedu.hit_data && window.Aedu.hit_data.id;

  // Don't track activity if we don't know the hit ID
  if (!HIT_ID) return false;

  // See if there is a previous duration for this hit. If so, add it to the timer by setting
  // the initialStartTime as the current time minus the previous duration.
  let initialStartTime = new Date();
  try {
    const previousDuration = window.sessionStorage.getItem(
      `${HIT_ID}:duration`,
    );
    if (previousDuration) {
      const parsedDuration = parseInt(previousDuration, 10);
      if (!isNaN(parsedDuration)) {
        initialStartTime = new Date(Date.now() - parsedDuration);
      }
    }
  } catch (_) {
    // Ignore errors and proceed
  }

  TimeMe.initialize({
    idleTimeoutInSeconds: 60,
    initialStartTime: initialStartTime,
  });
  // Don't start the timer until the page is visible. This addresses a known issue of the TimeMe
  // library where the timer starts when the page is loaded but not visible.
  TimeMe.stopTimer();

  // Track scroll position (at bottom of viewport)
  let scrollDistance = window.innerHeight;

  window.addEventListener(
    'scroll',
    throttle(() => {
      if (window.scrollY > scrollDistance) {
        scrollDistance = window.scrollY + window.innerHeight;
      }
    }, 500),
    { passive: true },
  );

  function sendPageActivityData(eventType: string) {
    const url = routes.api_v0_record_page_activity_path({
      subdomain_param: 'api',
    });

    const scrollDistanceLimit = Math.round(
      document.scrollingElement?.scrollHeight || document.body.scrollHeight,
    );
    // Clamp scroll distance to the limit, since some clients overscroll ("bounce") at the bottom.
    const clampedScrollDistance = Math.min(
      Math.round(scrollDistance),
      scrollDistanceLimit,
    );

    const data = {
      hit_id: HIT_ID,
      duration_ms: Math.round(TimeMe.getTimeOnCurrentPageInMilliseconds()) || 0,
      scroll_distance: clampedScrollDistance,
      scroll_distance_limit: scrollDistanceLimit,
      source_event: eventType,
    };
    window.sessionStorage.setItem(
      `${HIT_ID}:duration`,
      String(data.duration_ms),
    );
    keptAlivePost(url, data);
  }

  window.addEventListener('load', () => {
    if (document.visibilityState === 'visible' && document.hasFocus()) {
      TimeMe.startTimer();
    }
  });

  window.addEventListener('visibilitychange', (event) => {
    if (document.visibilityState === 'visible') {
      TimeMe.startTimer();
    } else if (document.visibilityState === 'hidden') {
      sendPageActivityData(event.type);
    }
  });

  window.addEventListener('pagehide', (event) => {
    sendPageActivityData(event.type);
  });

  // TODO: Temporary event type to track an upper bound on using
  // a polling approach instead of an event-based approach.
  sendPageActivityData('bundleload');
}
